import { WebArError, type WebArEvent } from "./contracts.js";
import type {
  CameraAuthorization,
  LoadedWebArEngine,
  WebArCameraPort,
  WebArCapabilityPort,
  WebArEnginePort,
  WebArEventPort,
  WebArReturnPort
} from "./controller.js";
import type { Xr8Runtime } from "./xr8-types.js";

declare global {
  interface Window {
    XR8?: Xr8Runtime;
  }
}

export interface BrowserCapabilitySignals {
  readonly secureContext: boolean;
  readonly cameraApi: boolean;
  readonly webAssembly: boolean;
  readonly webgl2: boolean;
  readonly mobileOrHeadset: boolean;
}

export function detectWebArCapability(
  signals: BrowserCapabilitySignals
): { readonly supported: boolean; readonly reason?: string } {
  if (!signals.secureContext) return { supported: false, reason: "secure_context_required" };
  if (!signals.cameraApi) return { supported: false, reason: "camera_api_unavailable" };
  if (!signals.webAssembly) return { supported: false, reason: "wasm_unavailable" };
  if (!signals.webgl2) return { supported: false, reason: "webgl2_unavailable" };
  if (!signals.mobileOrHeadset) return { supported: false, reason: "mobile_device_required" };
  return { supported: true };
}

export function createBrowserCapabilityPort(
  read: () => BrowserCapabilitySignals = () => {
    const userAgentData = (navigator as Navigator & {
      userAgentData?: { readonly mobile?: boolean };
    }).userAgentData;
    const ua = navigator.userAgent;
    const mobileOrHeadset = userAgentData?.mobile === true ||
      /Android|iPhone|iPad|iPod|Mobile|Quest|Vision/u.test(ua) ||
      (/Macintosh/u.test(ua) && navigator.maxTouchPoints > 1);
    const canvas = document.createElement("canvas");
    let webgl2 = false;
    try {
      webgl2 = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true }) !== null;
    } catch {
      webgl2 = false;
    }
    return {
      secureContext: window.isSecureContext,
      cameraApi: typeof navigator.mediaDevices?.getUserMedia === "function",
      webAssembly: typeof WebAssembly === "object",
      webgl2,
      mobileOrHeadset
    };
  }
): WebArCapabilityPort {
  return { detect: async () => detectWebArCapability(read()) };
}

export function createBrowserCameraPort(
  mediaDevices: Pick<MediaDevices, "getUserMedia"> = navigator.mediaDevices
): WebArCameraPort {
  return {
    async authorize(signal): Promise<CameraAuthorization> {
      if (signal.aborted) {
        throw new WebArError("cancelled", "Camera authorization was cancelled.", true);
      }
      let stream: MediaStream;
      try {
        stream = await mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" } }
        });
      } catch (cause) {
        if (signal.aborted) {
          throw new WebArError("cancelled", "Camera authorization was cancelled.", true, {
            cause
          });
        }
        throw new WebArError("camera_denied", "Camera access was not granted.", true, {
          cause
        });
      }
      for (const track of stream.getTracks()) track.stop();
      return { release() {} };
    }
  };
}

export interface BrowserEngineLoaderOptions {
  readonly window: Window;
  readonly document: Document;
  readonly engineUrl: string;
  readonly timeoutMs?: number;
}

export function createBrowserEngineLoader(options: BrowserEngineLoaderOptions): WebArEnginePort {
  let loaded: Promise<LoadedWebArEngine> | undefined;
  return {
    load(signal) {
      if (loaded) return loaded;
      loaded = new Promise<Xr8Runtime>((resolve, reject) => {
        if (signal.aborted) {
          reject(new WebArError("cancelled", "Engine loading was cancelled.", true));
          return;
        }
        if (options.window.XR8) {
          resolve(options.window.XR8);
          return;
        }

        const script = options.document.createElement("script");
        script.id = "customiznator-xr8-engine";
        script.src = options.engineUrl;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.dataset.preloadChunks = "slam";
        const timeout = options.window.setTimeout(() => {
          cleanup();
          script.remove();
          reject(new WebArError("engine_load_failed", "The AR engine timed out.", true));
        }, options.timeoutMs ?? 20_000);
        const cleanup = () => {
          options.window.clearTimeout(timeout);
          options.window.removeEventListener("xrloaded", onLoaded);
          signal.removeEventListener("abort", onAbort);
          script.removeEventListener("error", onError);
        };
        const onLoaded = () => {
          cleanup();
          if (options.window.XR8) resolve(options.window.XR8);
          else reject(new WebArError("engine_load_failed", "The AR engine did not initialize.", true));
        };
        const onError = () => {
          cleanup();
          script.remove();
          reject(new WebArError("engine_load_failed", "The AR engine could not be loaded.", true));
        };
        const onAbort = () => {
          cleanup();
          script.remove();
          reject(new WebArError("cancelled", "Engine loading was cancelled.", true));
        };
        options.window.addEventListener("xrloaded", onLoaded, { once: true });
        signal.addEventListener("abort", onAbort, { once: true });
        script.addEventListener("error", onError, { once: true });
        options.document.head.append(script);
      }).then(async (runtime) => {
        try {
          await runtime.loadChunk("slam");
        } catch (cause) {
          throw new WebArError("engine_load_failed", "World tracking could not be loaded.", true, {
            cause
          });
        }
        return { runtime };
      });
      loaded.catch(() => { loaded = undefined; });
      return loaded;
    }
  };
}

export function createBrowserEventPort(target: Window = window): WebArEventPort {
  return {
    emit(event: WebArEvent) {
      target.dispatchEvent(new CustomEvent<WebArEvent>("customiznator:webar", { detail: event }));
    }
  };
}

export function createBrowserReturnPort(target: Window = window): WebArReturnPort {
  return {
    returnToStorefront3d(origin) {
      const message = { type: "customiznator:webar-exit", destination: "storefront_3d" } as const;
      if (origin) {
        if (target.opener && !target.opener.closed) {
          target.opener.postMessage(message, origin);
          target.close();
          return;
        }
        if (target.parent !== target) {
          target.parent.postMessage(message, origin);
          return;
        }
      }
      if (target.history.length > 1) target.history.back();
    }
  };
}
